/**
 * Local snapshot cache — the client-side copy of the player's data.
 *
 * Purpose is *perceived speed*, not offline play. On a cold start the app can
 * render the player's real name, coins and depth from disk in one frame instead
 * of showing a loader for the length of a network round trip, then quietly
 * replace it with the server's answer.
 *
 * Three rules make that safe:
 *
 *   1. **The snapshot is never authoritative.** It seeds the UI and is
 *      overwritten by the first `/bootstrap` response. No mutation ever reads a
 *      balance from here — the wallet ledger lives on the server and the client
 *      is not allowed to decide what a player owns.
 *
 *   2. **It is scoped to a user.** Keyed by user uuid, so a shared device cannot
 *      show one player another's wallet, and signOut wipes it.
 *
 *   3. **It expires and it is versioned.** A stale snapshot is worse than none:
 *      showing a week-old coin count as if it were live is a bug report waiting
 *      to happen.
 */

const KEY = 'shwetap.snapshot';

/**
 * Bump when the shape changes. An old snapshot is then dropped rather than
 * being read with missing fields — cheaper and safer than a migration.
 */
const VERSION = 1;

/** Older than this and we wait for the server instead of showing stale numbers. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface Envelope<T> {
  v: number;
  /** User uuid this belongs to. */
  u: string;
  /** `Date.now()` at write time. */
  t: number;
  d: T;
}

/**
 * Read the snapshot for `userUuid`.
 *
 * Returns null when it is missing, too old, the wrong version, belongs to
 * someone else, or is unparseable — every failure is "no snapshot", never a
 * throw, because a corrupt cache must not stop the game from booting.
 */
export function readSnapshot<T>(userUuid: string | null): T | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;

    const env = JSON.parse(raw) as Envelope<T>;
    if (env.v !== VERSION) return null;
    if (userUuid != null && env.u !== userUuid) return null;
    if (Date.now() - env.t > MAX_AGE_MS) return null;

    return env.d ?? null;
  } catch {
    return null;
  }
}

/** Read whatever is cached without knowing the user yet — used on cold start. */
export function readAnySnapshot<T>(): { userUuid: string; data: T } | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const env = JSON.parse(raw) as Envelope<T>;
    if (env.v !== VERSION) return null;
    if (Date.now() - env.t > MAX_AGE_MS) return null;
    if (!env.u || env.d == null) return null;
    return { userUuid: env.u, data: env.d };
  } catch {
    return null;
  }
}

export function writeSnapshot<T>(userUuid: string, data: T): void {
  try {
    const env: Envelope<T> = { v: VERSION, u: userUuid, t: Date.now(), d: data };
    localStorage.setItem(KEY, JSON.stringify(env));
  } catch {
    /* Quota or private mode. The cache is an optimisation, so losing it is
       not an error worth surfacing. */
  }
}

/**
 * Wipe the snapshot.
 *
 * Called on sign-out. Leaving it behind would show the next player on a shared
 * device the previous player's name and balance before the first request lands.
 */
export function clearSnapshot(): void {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}
